import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import type { PvAbgang, PvAnbindung, PvPruefpunkt, PvStand, PvTrafo, PvVorgabe } from './elektro/pv';
import { PV_SPANNEN_HINWEIS, pruefstatusText } from './elektro/pv';
import { PDF_HINWEIS } from './elektro/sicherheitsstrom';
import { zerlegeBefund } from './elektro/sicherheitstechnik';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, ErrorState, KpiCard, KpiReihe, LoadingState, StatusBadge,
  type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

const zahl = (w: number | null | undefined, stellen = 0) =>
  w === null || w === undefined
    ? '—'
    : w.toLocaleString('de-DE', { maximumFractionDigits: stellen, minimumFractionDigits: stellen });

/**
 * PV-Anlage 1.000 kWp (W2-A3, 16.10.2026) — Abschnitt „Einspeisung und Netz".
 *
 * Die Seite LIEST `GET …/elektro/pv`: den PV-Trafo und seine Abgänge, die Netzanbindung im Modell,
 * Modulzahl, Wechselrichter, Einspeisung und Ertrag als Spannen aus der FLB 8.16, die Rückspeisung
 * über den Trafo und eine Prüfliste mit den Widersprüchen der FLB. Gerechnet wird auf dem Server, die
 * Seite schreibt nichts.
 *
 * Ein PDF-Knopf (ELT-PV) und die Excel-Mappe. Der PDF-Abruf legt nur bei geändertem Stand eine neue
 * Berichtsfassung an, sonst die abgelegte (F11) — das steht am Knopf und über der Seite.
 */
export function ElektroPv() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState />;
  return <PvAnsicht api={api} projektId={projekt.id} />;
}

function zustandVon(status: string): Zustand {
  return status === 'erfuellt' ? 'gut' : status === 'widerspruch' ? 'fehler' : status === 'offen' ? 'warnung' : 'info';
}

export function PvAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<PvStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    api.elektroPv(projektId)
      .then((s) => { if (aktiv) { setStand(s ?? null); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  async function laden(art: 'nachweis' | 'excel') {
    setLaeuft(art);
    try {
      if (art === 'nachweis') await api.elektroPvNachweisLaden(projektId);
      else await api.elektroPvListen(projektId);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  }

  const aktionen = (
    <>
      <button type="button" className="m-knopf" disabled={laeuft !== null}
              onClick={() => { void laden('excel'); }}>
        Excel-Mappe
      </button>
      <button type="button" className="m-knopf" disabled={laeuft !== null} title={PDF_HINWEIS}
              onClick={() => { void laden('nachweis'); }}>
        PV-Nachweis als PDF
      </button>
    </>
  );

  return (
    <div className="seite">
      <PageHeader
        titel="PV-Anlage"
        beschreibung="Netzanbindung der PV im Modell (MS, Trafo, NSHV), Modulzahl, Wechselrichter, Einspeisung und Ertrag als Spannen aus der FLB 8.16, Rückspeisung über den PV-Trafo und Prüfliste nach DIN VDE 0100-712 und VDE-AR-N 4110. Gerechnet beim Abruf, nur lesend."
        aktionen={aktionen}
      />
      <p className="hinweis">{PDF_HINWEIS}</p>
      {fehler && <ErrorState titel="Die PV-Anlage konnte nicht geladen werden." technisch={fehler} />}
      {!fehler && !stand && <LoadingState />}
      {stand && <PvInhalt stand={stand} />}
    </div>
  );
}

function PvInhalt({ stand }: { stand: PvStand }) {
  const k = stand.kennzahlen;
  const r = stand.bemessung;

  const pruef: Spalte<PvPruefpunkt>[] = [
    { schluessel: 'nr', titel: 'Nr', art: 'zahl', wert: (z) => z.nr },
    { schluessel: 'punkt', titel: 'Prüfpunkt', wert: (z) => z.punkt },
    {
      schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <StatusBadge zustand={zustandVon(z.status)} text={pruefstatusText(z.status)} klein />,
    },
    { schluessel: 'modell', titel: 'Modell und Rechnung', wert: (z) => z.modell },
    { schluessel: 'norm', titel: 'Normbezug', wert: (z) => z.normbezug },
  ];

  const trafos: Spalte<PvTrafo>[] = [
    { schluessel: 'k', titel: 'Trafo', wert: (z) => z.kennzeichen },
    { schluessel: 'b', titel: 'Bezeichnung', wert: (z) => z.bezeichnung },
    { schluessel: 'ms', titel: 'MS', wert: (z) => z.mittelspannung },
    { schluessel: 'raum', titel: 'MS-Raum', wert: (z) => z.msRaum },
    { schluessel: 'sr', titel: 'S_r', einheit: 'kVA', art: 'zahl', nachkomma: 0, wert: (z) => z.bemessungKva },
    { schluessel: 'uk', titel: 'u_k', einheit: '%', art: 'zahl', nachkomma: 1, wert: (z) => z.kurzschlussspannungProzent },
    { schluessel: 'last', titel: 'Last Abgänge', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.verbraucherBedarfW / 1000 },
    { schluessel: 'a', titel: 'Rückspeisung', einheit: '%', art: 'zahl', nachkomma: 1, wert: (z) => z.auslastungProzent },
  ];

  const abgaenge: Spalte<PvAbgang>[] = [
    { schluessel: 't', titel: 'Trafo', wert: (z) => z.trafo },
    { schluessel: 'k', titel: 'Abgang', wert: (z) => z.kennzeichen },
    { schluessel: 'a', titel: 'Art', wert: (z) => z.art },
    { schluessel: 'r', titel: 'Raum', wert: (z) => z.raumnummer },
    { schluessel: 'p', titel: 'Bedarf', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => (z.bedarfsleistungW === null ? null : z.bedarfsleistungW / 1000) },
    { schluessel: 'u', titel: 'Unterverteiler', art: 'zahl', wert: (z) => z.unterverteiler },
  ];

  const anbindung: Spalte<PvAnbindung>[] = [
    { schluessel: 'art', titel: 'Bauteilart', wert: (z) => z.bauteilart },
    { schluessel: 'b', titel: 'Bezeichnung', wert: (z) => z.bezeichnung },
    { schluessel: 't', titel: 'Typ', wert: (z) => z.typ },
    { schluessel: 'r', titel: 'Raum', wert: (z) => z.raumnummer },
    { schluessel: 'v', titel: 'Verteiler', wert: (z) => z.verteiler },
    { schluessel: 'g', titel: 'Grund', wert: (z) => z.grund },
  ];

  const vorgaben: Spalte<PvVorgabe>[] = [
    { schluessel: 'f', titel: 'Vorgabe', wert: (z) => z.feld },
    { schluessel: 'w', titel: 'Wert', wert: (z) => `${z.wert} ${z.einheit ?? ''}`.trim() },
    { schluessel: 'h', titel: 'Herkunft', wert: (z) => z.herkunft + (z.rueckfall ? ' (Rückfall)' : '') },
    { schluessel: 'b', titel: 'bestätigt', wert: (z) => (z.bestaetigt ? 'ja' : 'nein') },
    { schluessel: 'n', titel: 'Normbezug', wert: (z) => z.normbezug },
  ];

  return (
    <>
      <p className="hinweis hinweis-warnung">{PV_SPANNEN_HINWEIS}</p>
      <KpiReihe>
        <KpiCard titel="Generator P_DC" wert={`${zahl(r.leistungKwp)} kWp`}
                 vergleich={`${zahl(r.moduleMin)} … ${zahl(r.moduleMax)} Module · im Modell ${k.module}`} />
        <KpiCard titel="Einspeisung S_AC" wert={`${zahl(r.scheinleistungKva)} kVA`}
                 vergleich={`P_AC ${zahl(r.leistungAcKw)} kW · I ${zahl(r.stromA)} A`} />
        <KpiCard titel="Wechselrichter" wert={`${r.wechselrichterMin} … ${r.wechselrichterMax}`}
                 vergleich={`im Modell ${k.wechselrichter}`} />
        <KpiCard titel="Ertrag" wert={`${zahl(r.ertragMinMwh)} … ${zahl(r.ertragMaxMwh)} MWh/a`} vergleich="Spanne aus der FLB" />
        <KpiCard titel="Prüfpunkte offen" wert={k.pruefpunkteOffen}
                 vergleich={`${k.widersprueche} Widersprüche`}
                 zustand={k.widersprueche > 0 ? 'fehler' : k.pruefpunkteOffen > 0 ? 'warnung' : 'gut'}
                 zustandText={k.widersprueche > 0 ? 'klären' : k.pruefpunkteOffen > 0 ? 'offen' : 'erfüllt'} />
        <KpiCard titel="Warnungen" wert={k.warnungen} vergleich={`${k.befunde} Befunde`} />
      </KpiReihe>

      <ContentSection titel="Befunde">
        <ul className="liste">
          {stand.befunde.map((b, i) => {
            const { stufe, text } = zerlegeBefund(b);
            const z: Zustand = stufe === 'fehler' ? 'fehler' : stufe === 'warnung' ? 'warnung' : 'info';
            return <li key={`${i}-${text.slice(0, 24)}`}><StatusBadge zustand={z} text={text} /></li>;
          })}
        </ul>
      </ContentSection>

      <ContentSection titel="Prüfliste">
        <DataTable name="Prüfliste" spalten={pruef} zeilen={stand.pruefliste} schluessel={(z) => String(z.nr)}
                   suche={false} dicht />
        <p className="hinweis">{r.rechenweg.join(' · ')}</p>
      </ContentSection>

      <ContentSection titel="PV-Trafo und seine Abgänge">
        <DataTable name={`PV-Trafo (${stand.trafos.length})`} spalten={trafos} zeilen={stand.trafos}
                   schluessel={(z) => z.kennzeichen} suche={false} dicht />
        <DataTable name={`Abgänge (${stand.abgaenge.length})`} spalten={abgaenge} zeilen={stand.abgaenge}
                   schluessel={(z) => `${z.trafo}-${z.kennzeichen}`} suche={false} dicht />
      </ContentSection>

      <ContentSection titel="Netzanbindung im Modell">
        <DataTable name={`MS- und NSHV-Felder (${stand.anbindung.length})`} spalten={anbindung}
                   zeilen={stand.anbindung} schluessel={(z) => `${z.raumnummer}-${z.typ}-${z.bezeichnung}`}
                   suche={false} dicht />
      </ContentSection>

      <ContentSection titel="Vorgaben">
        <DataTable name="Vorgaben" spalten={vorgaben} zeilen={stand.vorgaben} schluessel={(z) => z.feld}
                   suche={false} dicht />
      </ContentSection>
    </>
  );
}
